import type { RefObject } from "react"
import { useEffect, useState } from "react"

// Menus, popovers and tooltips the composer opens render in portals outside it.
const FLOATING_LAYER_SELECTOR = [
	"[data-radix-popper-content-wrapper]",
	"[data-tippy-root]",
	"[role='dialog']",
	"[role='menu']",
	"[role='listbox']",
].join(",")

export const isInsideComposer = (
	target: Element,
	container: HTMLElement,
): boolean => {
	if (container.contains(target)) return true
	const layer = target.closest(FLOATING_LAYER_SELECTOR)
	// A drawer that holds the whole thread is a dialog too; only a layer
	// floating above the composer counts as part of it.
	return !!layer && !layer.contains(container)
}

const hasFocusWithin = (container: HTMLElement | null): boolean =>
	!!container && container.contains(document.activeElement)

/**
 * On a phone the composer stays collapsed to a short preview until it takes
 * focus, and collapses again when the agent taps or scrolls the thread.
 */
export function useComposerCollapse({
	containerRef,
	enabled,
	resetKey,
}: {
	containerRef: RefObject<HTMLElement | null>
	enabled: boolean
	resetKey: string
}): { collapsed: boolean } {
	const [expanded, setExpanded] = useState(false)

	useEffect(() => {
		setExpanded(hasFocusWithin(containerRef.current))
	}, [resetKey, containerRef])

	useEffect(() => {
		const container = containerRef.current
		if (!enabled || !container) return
		if (hasFocusWithin(container)) setExpanded(true)

		const onFocusIn = () => setExpanded(true)
		// A blur with nowhere to go (iOS keyboard dismissed, a tap on a toolbar
		// button Safari won't focus) keeps it open; collapsing there would move
		// the button out from under the finger before its click fires.
		const onFocusOut = (event: FocusEvent) => {
			const next = event.relatedTarget
			if (!(next instanceof Element)) return
			if (isInsideComposer(next, container)) return
			setExpanded(false)
		}
		const onPointerDown = (event: PointerEvent) => {
			const target = event.target
			if (!(target instanceof Element)) return
			if (isInsideComposer(target, container)) return
			setExpanded(false)
			const active = document.activeElement
			if (active instanceof HTMLElement && container.contains(active)) {
				active.blur()
			}
		}

		container.addEventListener("focusin", onFocusIn)
		container.addEventListener("focusout", onFocusOut)
		document.addEventListener("pointerdown", onPointerDown, true)
		return () => {
			container.removeEventListener("focusin", onFocusIn)
			container.removeEventListener("focusout", onFocusOut)
			document.removeEventListener("pointerdown", onPointerDown, true)
		}
	}, [enabled, containerRef])

	return { collapsed: enabled && !expanded }
}
